import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import type { SelectionField } from '../../state/useSelectionContext';
import { Frame, HeadRow, SubCard } from './primitives';

/**
 * overview (docs/momentum-v3-slides.md "overview", retitled "Industry
 * context"): FULL light, 2x2 sub-cards. The strategy-kernel panel and the
 * "Next: The journey framework" / funnel footer are gone (both duplicated
 * the dedicated `strategy` slide); the four cards are industry/category
 * focused rather than client-observation focused, with FIXED titles (not
 * editable in v3 - `cardTitle1..4` is a v2-only concept) bound to their own
 * `content.fields` keys. Old decks that still carry `whatWeHeard` /
 * `painPoints` / `goals` are ignored: v3 decks are always freshly built, so
 * there is no migration path to preserve.
 *
 * Title is hardcoded rather than read from `section.title` (same convention
 * `MediaMixPage` uses for "Media mix") since the retitle is fixed chrome,
 * not rep-editable copy, and stream 1's assembly retitle may not have landed
 * in every existing deck.
 */
const CARDS: { fieldKey: 'industryContext' | 'demandBehavior' | 'marketChallenge' | 'campaignOpportunity'; title: string; placeholder: string }[] = [
  { fieldKey: 'industryContext', title: 'Industry context', placeholder: "What matters about this category or market" },
  { fieldKey: 'demandBehavior', title: 'Demand behavior', placeholder: 'How customers enter the market or decide' },
  { fieldKey: 'marketChallenge', title: 'Market challenge', placeholder: 'The key difficulty the campaign must overcome' },
  { fieldKey: 'campaignOpportunity', title: 'Campaign opportunity', placeholder: 'What this creates an opportunity to do' },
];

export function OverviewPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const fields = section.content.fields ?? {};
  const setField = (key: string, value: string) => {
    actions.saveContent(section, { fields: { ...fields, [key]: value } });
  };

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-overview-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-full-pane">
          <HeadRow title="Industry context" />
          <div className="mv3-overview-grid">
            {CARDS.map((card, index) => (
              <SubCard tone="light" key={card.fieldKey} className="mv3-overview-card">
                <div className="mv3-ov-card-head">
                  <span className="mv3-ov-card-num">{String(index + 1).padStart(2, '0')}</span>
                  <span className="mv3-ov-card-title">{card.title}</span>
                </div>
                <div className="mv3-ov-card-body">
                  <EditableText
                    value={fields[card.fieldKey]}
                    field={`fields.${card.fieldKey}` as SelectionField}
                    placeholder={card.placeholder}
                    readOnly={readOnly}
                    onSave={(next) => setField(card.fieldKey, next)}
                  />
                </div>
              </SubCard>
            ))}
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
